import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { emailToUsername, formatDateTime } from '../utils/formatters';
import './TestRunListPage.css';

interface TestRunSummary {
  id: string;
  name: string;
  folder_id: string;
  status: string;
  created_at: string;
  created_by: string;
  total_cases: number;
  pass_count: number;
  fail_count: number;
  not_run_count: number;
  progress_percentage: number;
}

const TestRunListPage: React.FC = () => {
  const navigate = useNavigate();
  const [testRuns, setTestRuns] = useState<TestRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTestRuns();
  }, [statusFilter]);

  const loadTestRuns = async () => {
    try {
      setLoading(true);
      const runs = await api.listTestRuns(undefined, statusFilter || undefined);
      setTestRuns(runs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test runs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'NOT_STARTED': return 'status-not-started';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'COMPLETED': return 'status-completed';
      case 'ABORTED': return 'status-aborted';
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NOT_STARTED': return 'Not Started';
      case 'IN_PROGRESS': return 'In Progress';
      case 'COMPLETED': return 'Completed';
      case 'ABORTED': return 'Aborted';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString);
  };

  if (loading) {
    return <div className="test-run-list-page"><div className="loading">Loading test runs...</div></div>;
  }

  return (
    <div className="test-run-list-page">
      <div className="page-header">
        <h1>Test Runs</h1>
        <button className="btn-primary" onClick={() => navigate('/test-runs/create')}>
          + Create Test Run
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters">
        <label>
          Status:
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="ABORTED">Aborted</option>
          </select>
        </label>
      </div>

      {testRuns.length === 0 ? (
        <div className="empty-state">
          <p>No test runs found. Create your first test run to start executing test cases.</p>
        </div>
      ) : (
        <div className="test-runs-grid">
          {testRuns.map((run) => (
            <div key={run.id} className="test-run-card" onClick={() => navigate(`/test-runs/${run.id}`)}>
              <div className="card-header">
                <h3>{run.name}</h3>
                <span className={`status-badge ${getStatusBadgeClass(run.status)}`}>
                  {getStatusLabel(run.status)}
                </span>
              </div>
              
              <div className="card-body">
                <div className="progress-section">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${run.progress_percentage}%` }}
                    />
                  </div>
                  <span className="progress-text">{run.progress_percentage.toFixed(0)}% Complete</span>
                </div>

                <div className="metrics">
                  <div className="metric">
                    <span className="metric-label">Total</span>
                    <span className="metric-value">{run.total_cases}</span>
                  </div>
                  <div className="metric pass">
                    <span className="metric-label">Pass</span>
                    <span className="metric-value">{run.pass_count}</span>
                  </div>
                  <div className="metric fail">
                    <span className="metric-label">Fail</span>
                    <span className="metric-value">{run.fail_count}</span>
                  </div>
                  <div className="metric not-run">
                    <span className="metric-label">Not Run</span>
                    <span className="metric-value">{run.not_run_count}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <span className="created-info">
                    Created by {emailToUsername(run.created_by)} on {formatDate(run.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestRunListPage;
